import type { Placement } from '@popperjs/core';
import { useIsMobileLayout } from '@src/hooks';
import { MixpanelEventSource, MixpanelSourceProvider } from '@talos/kyoko';
import { memo, useEffect, type RefObject } from 'react';
import { MarketSelectorContextProvider, useMarketSelectorContext } from './MarketSelectorContext';
import { MarketSelectorPanel, type MarketSelectorPanelProps } from './MarketSelectorPanel';
import { MobileMarketSelectorSheet } from './mobile/MobileMarketSelectorSheet';
import { NarrowMarketSelectorPanel } from './NarrowMarketSelectorPanel';
import type { MarketSelectorAPI, MarketSelectorExtensionFactory } from './types';
import { useMarketSelectorPanel } from './useMarketSelectorPanel';

export interface MarketSelectorPanelOnlyProps extends Pick<
  MarketSelectorPanelProps,
  'panelBottomSuffix' | 'panelContentKey' | 'panelTopPrefix' | 'testID'
> {
  marketSelectorAPI: MarketSelectorAPI;
  isOpen: boolean;
  onClose: () => void;
  panelAnchorRef: RefObject<HTMLElement>;
  panelPlacement?: Placement;
  panelOffset?: number;
  isNarrow?: boolean;
  showCredentialsOnlyToggle?: boolean;
  extensions?: MarketSelectorExtensionFactory[];
  showBlotterOnOpen?: boolean;
}

/**
 * Renders only the Market Selector panel (dropdown) without the chrome (buttons, selections list).
 * Used for quick-access from menu/footer where we don't want the full MarketSelector UI.
 */
export const MarketSelectorPanelOnly = memo(function MarketSelectorPanelOnly({
  marketSelectorAPI,
  isOpen,
  onClose,
  panelAnchorRef,
  panelPlacement = 'auto',
  panelOffset = 8,
  isNarrow = false,
  showCredentialsOnlyToggle = false,
  extensions,
  panelTopPrefix,
  panelBottomSuffix,
  panelContentKey,
  testID,
  showBlotterOnOpen = false,
}: MarketSelectorPanelOnlyProps) {
  const isMobileLayout = useIsMobileLayout();

  return (
    <MarketSelectorContextProvider
      marketSelectorAPI={marketSelectorAPI}
      isNarrow={isNarrow || isMobileLayout}
      showCredentialsOnlyToggle={showCredentialsOnlyToggle || isMobileLayout}
      extensions={extensions}
    >
      <MixpanelSourceProvider value={MixpanelEventSource.MarketAccountSelector}>
        {isMobileLayout ? (
          <MobileMarketSelectorSheet
            isOpen={isOpen}
            onClose={onClose}
            panelTopPrefix={panelTopPrefix}
            panelBottomSuffix={panelBottomSuffix}
            panelContentKey={panelContentKey}
            testID={testID}
          />
        ) : (
          <InnerPanelOnly
            isOpen={isOpen}
            onClose={onClose}
            panelAnchorRef={panelAnchorRef}
            panelPlacement={panelPlacement}
            panelOffset={panelOffset}
            panelTopPrefix={panelTopPrefix}
            panelBottomSuffix={panelBottomSuffix}
            panelContentKey={panelContentKey}
            testID={testID}
            showBlotterOnOpen={showBlotterOnOpen}
          />
        )}
      </MixpanelSourceProvider>
    </MarketSelectorContextProvider>
  );
});

interface InnerPanelOnlyProps {
  isOpen: boolean;
  onClose: () => void;
  panelAnchorRef: RefObject<HTMLElement>;
  panelPlacement: Placement;
  panelOffset: number;
  panelTopPrefix?: MarketSelectorPanelProps['panelTopPrefix'];
  panelBottomSuffix?: MarketSelectorPanelProps['panelBottomSuffix'];
  panelContentKey?: MarketSelectorPanelProps['panelContentKey'];
  testID?: string;
  showBlotterOnOpen: boolean;
}

function InnerPanelOnly({
  isOpen,
  onClose,
  panelAnchorRef,
  panelPlacement,
  panelOffset,
  panelTopPrefix,
  panelBottomSuffix,
  panelContentKey,
  testID,
  showBlotterOnOpen,
}: InnerPanelOnlyProps) {
  const { isNarrow, setIsOpen } = useMarketSelectorContext();

  useEffect(() => {
    setIsOpen(isOpen);
  }, [isOpen, setIsOpen]);

  const dropdownProps = useMarketSelectorPanel({
    isOpen,
    close: onClose,
    panelAnchorRef,
    panelPlacement,
    panelOffset,
  });

  if (!isOpen) {
    return null;
  }

  return isNarrow ? (
    <NarrowMarketSelectorPanel
      dropdownProps={dropdownProps}
      panelTopPrefix={panelTopPrefix}
      panelBottomSuffix={panelBottomSuffix}
      panelContentKey={panelContentKey}
      testID={testID}
      showBlotterOnOpen={showBlotterOnOpen}
    />
  ) : (
    <MarketSelectorPanel
      dropdownProps={dropdownProps}
      panelTopPrefix={panelTopPrefix}
      panelBottomSuffix={panelBottomSuffix}
      panelContentKey={panelContentKey}
      testID={testID}
    />
  );
}
